import { useMemo } from "react";
import SaltoIqsView from "./SaltoIqsView";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import { useLanguageContext } from "../../../../context/LanguageContext";

function SaltoIqs({ configData, setConfigData, saltoResources }) {
  const { translate } = useLanguageContext();
  const callEvaApi = useEvaApi();

  const iqLabels = useMemo(() => {
    return saltoResources.iqs.reduce((labels, iq) => {
      labels[iq.id] = iq.label;
      return labels;
    }, {});
  }, [saltoResources.iqs]);

  const availableIqs = useMemo(() => {
    return saltoResources.iqs.filter((iq) => {
      return !configData.iqs.some((configIq) => configIq.iq_id === iq.id);
    });
  }, [saltoResources.iqs, configData]);

  /**
   * This method will add a iq to the config data
   * @param {string} iqId
   */
  function onIqAdd(iqId) {
    setConfigData((prevConfigData) => ({
      ...prevConfigData,
      iqs: [...prevConfigData.iqs, { iq_id: iqId }],
    }));
  }

  /**
   * This method will update the pin for a iq in the config data
   * @param {string} iqId
   * @param {string} newPin
   */
  function onIqPinChange(iqId, newPin) {
    setConfigData((prevConfigData) => ({
      ...prevConfigData,
      iqs: prevConfigData.iqs.map((iq) =>
        iq.iq_id === iqId ? { ...iq, pin: newPin } : iq,
      ),
    }));
  }

  /**
   * This method will delete a iq from the config data
   * @param {string} iqId
   */
  function onIqDelete(iqId) {
    const configIq = configData.iqs.find((iq) => iq.iq_id === iqId);
    if (
      configIq?.connected &&
      !window.confirm(translate("eva.main.questions.delete"))
    ) {
      return;
    }

    setConfigData((prevConfigData) => ({
      ...prevConfigData,
      iqs: prevConfigData.iqs.filter((iq) => iq.iq_id !== iqId),
    }));
  }

  /**
   * This method will initiate the connection process for a iq
   * @param {string} iqId
   */
  async function onIqConnectionInit(iqId) {
    await callEvaApi(
      new EvaApiCall(`integrations/salto/iqs/${iqId}/connection/initiate`)
        .setLoadingGroup(`salto_iq_connection_${iqId}`)
        .setMethod("POST")
        .setAlertError(true)
        .setOnSuccess((response) => {
          setConfigData((prevConfigData) => ({
            ...prevConfigData,
            iqs: prevConfigData.iqs.map((iq) =>
              iq.iq_id === iqId ? { ...iq, connection_initiated: true } : iq,
            ),
          }));
        }),
    );
  }

  /**
   * This method will complete the connection process for a iq
   * @param {string} iqId
   */
  async function onIqConnect(iqId) {
    const configIq = configData.iqs.find((iq) => iq.iq_id === iqId);
    if (!configIq) return;

    await callEvaApi(
      new EvaApiCall(`integrations/salto/iqs/${iqId}/connection/confirm`)
        .setLoadingGroup(`salto_iq_connection_${iqId}`)
        .setMethod("POST")
        .setData({ pin: configIq.pin })
        .setAlertError(true)
        .setOnSuccess((response) => {
          setConfigData((prevConfigData) => ({
            ...prevConfigData,
            iqs: prevConfigData.iqs.map((iq) =>
              iq.iq_id === iqId
                ? { ...iq, connected: true, connection_initiated: false }
                : iq,
            ),
          }));
        }),
    );
  }

  return (
    <SaltoIqsView
      configData={configData}
      iqLabels={iqLabels}
      availableIqs={availableIqs}
      onIqAdd={onIqAdd}
      onIqPinChange={onIqPinChange}
      onIqDelete={onIqDelete}
      onIqConnectionInit={onIqConnectionInit}
      onIqConnect={onIqConnect}
    />
  );
}

export default SaltoIqs;
